import type { PropsWithChildren, ReactNode } from 'react';
import type { ButtonActionProps } from '../button/types';

export type SortDirection = 'asc' | 'desc';

export type DataTableOption<TId extends string = string> = {
  id: TId;
  label: string;
};

export type DataTableSortActive<TSortId extends string = string> = {
  id: TSortId;
  direction: SortDirection;
};

// The skeleton geometry is the same for both tables, so it lives with `Table`.
export { DEFAULT_SKELETON_COLS, DEFAULT_SKELETON_ROWS } from '../table/types';

const DEFAULT_PAGE_SIZE = 10;
export const DEFAULT_PAGE_SIZE_OPTIONS = [DEFAULT_PAGE_SIZE, 25, 100];

export type DataTableSearch = {
  value: string;
  placeholder?: string;
  onChange: (value: string) => void;
};

export type DataTableSort<TSortId extends string = string> = {
  options: readonly DataTableOption<TSortId>[];
  value: DataTableSortActive<TSortId> | undefined;
  onChange: (value: DataTableSortActive<TSortId> | undefined) => void;
};

export type DataTableFilter<TValue extends string = string> = {
  id: string;
  label: string;
  options: readonly DataTableOption<TValue>[];
  value: TValue[];
  multiple?: boolean;
  pinned?: boolean;
  onChange: (value: TValue[]) => void;
};

type DataTableView<TValue extends string = string> = DataTableOption<TValue> & {
  count?: number;
};

export type DataTableViews<TViewId extends string = string> = {
  items: readonly DataTableView<TViewId>[];
  value: TViewId;
  onChange: (value: TViewId) => void;
};

export type DataTableColumnVisibility<TColumnId extends string = string> = {
  value: readonly TColumnId[];
  columnSelectorEnabled?: boolean;
  onChange: (value: TColumnId[]) => void;
};

export type DataTableExpansion = {
  value: readonly string[];
  onChange: (value: string[]) => void;
};

export type DataTablePagination = {
  page: number;
  total: number;
  pageSize: number;
  pageSizeOptions?: number[];
  onPageChange?: (state: { page: number; pageSize: number }) => void;
};

type DataTableSkeleton = {
  /** Number of placeholder rows to render (default: 5) */
  rows?: number;
  /** Number of columns when headings are not provided (default: 4) */
  cols?: number;
};

export type DataTableColumnWidth = 'none' | 'sm' | 'md' | 'lg';

export type DataTableColumn<TData = unknown> = {
  id: string;
  label?: string;
  alwaysVisible?: boolean;
  sticky?: boolean;
  maxWidth?: DataTableColumnWidth;
  /** Cell renders its own controls, so `onRowClick` does not extend over it. */
  interactive?: boolean;
  renderCell: (row: TData, index: number) => ReactNode;
};

export type DataTableRowData<TData = unknown> = {
  rows: TData[];
  getRowId: (row: TData, index: number) => string;
  getSubRows?: (row: TData) => TData[] | undefined;
};

export type DataTableCellProps = PropsWithChildren<{
  align?: 'start' | 'end';
}>;

export type DataTableRowAction = ButtonActionProps & {
  id?: string;
  label: string;
  hidden?: boolean;
  variant?: 'primary' | 'secondary';
  danger?: boolean;
  inMoreMenu?: boolean;
  collapsible?: boolean;
};

export type DataTableBulkAction<TData = unknown> = Omit<DataTableRowAction, 'onClick'> & {
  onClick?: (rows: TData[]) => void;
};

type DataTableSelectAll = {
  value: boolean;
  onChange: (value: boolean) => void;
  total?: number;
};

export type DataTableMultiSelect<TData = unknown> = {
  value: readonly string[];
  bulkActions?: DataTableBulkAction<TData>[];
  selectAll?: DataTableSelectAll;
  onChange: (value: string[]) => void;
};

export type DataTableProps<
  TData = unknown,
  TColumns extends readonly DataTableColumn<TData>[] = readonly DataTableColumn<TData>[],
  TSortId extends string = string,
  TViewId extends string = string,
> = {
  columns: TColumns;
  data?: TData[];
  caption: string;
  columnVisibility?: DataTableColumnVisibility<TColumns[number]['id']>;
  expansion?: DataTableExpansion;
  multiSelect?: DataTableMultiSelect<TData>;
  views?: DataTableViews<TViewId>;
  search?: DataTableSearch;
  filters?: readonly DataTableFilter[];
  sort?: DataTableSort<TSortId>;
  pagination?: DataTablePagination;
  loading?: boolean;
  skeleton?: DataTableSkeleton;
  getRowId: (row: TData, index: number) => string;
  getSubRows?: (row: TData) => TData[] | undefined;
  onRowClick?: (row: TData) => void;
};

// Model — what useDataTable returns to the themed renderers.
export type DataTablePaginationModel = {
  page: number;
  totalPages: number;
  pageSize: number;
  total?: number;
  pageSizeOptions?: number[];
  disabled?: boolean;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
};

export type DataTableSelectAllModel = {
  active: boolean;
  total?: number;
  apply: () => void;
};

export type DataTableRowModel<TData = unknown> = {
  id: string;
  data: TData;
  depth: number;
  hasChildren: boolean;
  expanded: boolean;
  onToggleExpand?: () => void;
};

export type DataTableSelectionModel<TData = unknown> = {
  selectedIds: Set<string>;
  selectedRows: TData[];
  count: number;
  allSelected: boolean;
  someSelected: boolean;
  selectAll?: DataTableSelectAllModel;
  isSelected: (id: string) => boolean;
  isIndeterminate: (id: string) => boolean;
  toggle: (id: string) => void;
  toggleAll: () => void;
  clear: () => void;
};

export type DataTableModel<
  TData = unknown,
  TColumns extends readonly DataTableColumn<TData>[] = readonly DataTableColumn<TData>[],
> = {
  columns: {
    visible: DataTableColumn<TData>[];
    toggleVisible: (id: TColumns[number]['id']) => void;
  };
  rows?: DataTableRowModel<TData>[];
  isTree: boolean;
  selection?: DataTableSelectionModel<TData>;
  pagination?: DataTablePaginationModel;
};

// Shared by each theme's internal Row and SkeletonRow so the copies cannot drift.
export type DataTableRowProps<TData = unknown> = {
  row: DataTableRowModel<TData>;
  index: number;
  columns: DataTableColumn<TData>[];
  isTree: boolean;
  selected?: boolean;
  onClick?: () => void;
};

export type DataTableSkeletonRowProps = {
  cols: number;
  rowIndex: number;
};
